<template>
  <div>
    <v-overlay v-model="loading">
      <v-progress-circular
        indeterminate
        size="40"
        color="primary"
      ></v-progress-circular>
    </v-overlay>

    <!-- Header with Select All -->
    <v-row
      class="white rounded-xl px-5 font-weight-bold mb-4 text-left align-center"
    >
      <v-col cols="12" class="d-flex align-center k-heading">
        <v-checkbox
          v-model="select_all"
          class="mr-3"
          @click="selectAll"
          id="select_all"
        ></v-checkbox>
        <span class="k-heading-title">Received Recordings</span>
        <delete-selected
          class="ml-6"
          v-show="selected_ids.length >= 2"
          :selected_ids="selected_ids"
        ></delete-selected>
      </v-col>
    </v-row>

    <!-- Loading Skeleton -->
    <template v-if="skeloading">
      <v-row>
        <v-col v-for="n in 5" :key="n" class="five-card-col">
          <v-card class="recording-card-skeleton" elevation="2">
            <v-skeleton-loader
              type="image, card-heading, text"
              height="280"
            ></v-skeleton-loader>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <!-- No Recording Found -->
    <template v-else-if="recordings.length === 0">
      <v-col cols="12" class="text-center py-10">
        <v-icon size="80" color="grey lighten-2">mdi-video-off</v-icon>
        <p class="no-recording-message text-h6 mt-4">No recording found!</p>
      </v-col>
    </template>

    <!-- Recording Cards -->
    <template v-else>
      <v-row class="five-cards-row">
        <v-col
          v-for="(record, index) in recordings"
          :key="index"
          class="five-card-col"
        >
          <v-card
            class="recording-card"
            elevation="2"
            @mouseenter="hoveredCard = record.id"
            @mouseleave="hoveredCard = null"
          >
            <!-- Card Header with Checkbox (Hidden unless hover or checked) -->
            <div
              v-show="
                hoveredCard === record.id || selected_ids.includes(record.id)
              "
              class="card-header"
            >
              <v-checkbox
                :value="record.id"
                v-model="selected_ids"
                class="card-checkbox"
                hide-details
              ></v-checkbox>
            </div>

            <!-- Video Thumbnail Area -->
            <div class="video-thumbnail" @click="showDetail(record)">
              <!-- Video Preview -->
              <div class="video-preview">
                <video
                  :src="record.video_url || record.file_url"
                  class="preview-video"
                  muted
                  preload="metadata"
                  :poster="record.thumbnail_url"
                  @loadeddata="onVideoLoaded"
                  @error="onVideoError"
                ></video>
                <!-- Fallback placeholder when video fails to load -->
                <div
                  v-if="!record.video_url && !record.file_url"
                  class="video-placeholder-fallback"
                >
                  <v-icon size="60" color="white"
                    >mdi-play-circle-outline</v-icon
                  >
                </div>
              </div>
              <!-- Play overlay -->
              <div class="play-overlay">
                <v-icon size="60" color="white">mdi-play-circle-outline</v-icon>
              </div>
              <div class="video-duration">
                {{ formatTime(record.duration) }}
              </div>
              <div class="video-label">{{ record["category"]["name"] }}</div>

              <!-- Hover Menu -->
              <div v-show="hoveredCard === record.id" class="hover-menu">
                <v-menu offset-y>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      v-on="on"
                      icon
                      small
                      class="menu-trigger-btn"
                      @click.stop
                    >
                      <v-icon color="white">mdi-dots-horizontal</v-icon>
                    </v-btn>
                  </template>
                  <v-list class="k-folder-dropdown">
                    <v-list-item
                      class="k-folder-dropdown-item"
                      @click="editDialog(record)"
                    >
                      <v-list-item-title>
                        <svg
                          aria-hidden="true"
                          focusable="false"
                          data-prefix="far"
                          data-icon="pen-to-square"
                          class="svg-inline--fa fa-pen-to-square"
                          role="img"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 512 512"
                        >
                          <path
                            fill="currentColor"
                            d="M441 58.9L453.1 71c9.4 9.4 9.4 24.6 0 33.9L424 134.1 377.9 88 407 58.9c9.4-9.4 24.6-9.4 33.9 0zM209.8 256.2L344 121.9 390.1 168 255.8 302.2c-2.9 2.9-6.5 5-10.4 6.1l-58.5 16.7 16.7-58.5c1.1-3.9 3.2-7.5 6.1-10.4zM373.1 25L175.8 222.2c-8.7 8.7-15 19.4-18.3 31.1l-28.6 100c-2.4 8.4-.1 17.4 6.1 23.6s15.2 8.5 23.6 6.1l100-28.6c11.8-3.4 22.5-9.7 31.1-18.3L487 138.9c28.1-28.1 28.1-73.7 0-101.8L474.9 25C446.8-3.1 401.2-3.1 373.1 25zM88 64C39.4 64 0 103.4 0 152L0 424c0 48.6 39.4 88 88 88l272 0c48.6 0 88-39.4 88-88l0-112c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 112c0 22.1-17.9 40-40 40L88 464c-22.1 0-40-17.9-40-40l0-272c0-22.1 17.9-40 40-40l112 0c13.3 0 24-10.7 24-24s-10.7-24-24-24L88 64z"
                          ></path>
                        </svg>
                        Edit Message
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      class="k-folder-dropdown-item"
                      @click="callShareRecording(record)"
                    >
                      <v-list-item-title>
                        <v-icon>mdi-share-variant</v-icon>
                        Share
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      class="k-folder-dropdown-item"
                      @click="callMoveRecordingToFolder(record)"
                    >
                      <v-list-item-title>
                        <v-icon>mdi-folder-move</v-icon>
                        Move
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      class="k-folder-dropdown-item"
                      @click="handleDuplicateRecording(record)"
                    >
                      <v-list-item-title>
                        <v-icon>mdi-content-copy</v-icon>
                        Duplicate
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      class="k-folder-dropdown-item"
                      @click="callArchiveRecording(record)"
                    >
                      <v-list-item-title>
                        <v-icon>mdi-archive</v-icon>
                        Archive
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      class="k-folder-dropdown-item"
                      @click="openDownloadingDialog(record)"
                    >
                      <v-list-item-title>
                        <v-icon>mdi-download</v-icon>
                        Download
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      class="k-folder-dropdown-item"
                      @click="deleteRecordingDialog(record)"
                    >
                      <v-list-item-title>
                        <v-icon>mdi-delete</v-icon>
                        Delete
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </div>

            <!-- Card Content -->
            <v-card-text class="card-content">
              <!-- Recording Title -->
              <h4 class="recording-title" @click="showDetail(record)">
                <span class="title-text" :title="record.title">{{
                  truncateTitle(record.title)
                }}</span>
                <span class="separator">⋅</span>
                <span class="time-ago">{{ record.date_recorded }}</span>
              </h4>

              <!-- Stats -->
              <div class="recording-stats">
                <span class="stat-item">
                  <v-icon size="16" color="grey">mdi-eye</v-icon>
                  {{ record.total_views || 0 }}
                </span>
                <span class="stat-item">
                  <v-icon size="16" color="grey">mdi-comment</v-icon>
                  {{ record.comments_count || 0 }}
                </span>
                <!-- <span class="stat-item">
                  <v-icon size="16" color="grey">mdi-heart</v-icon>
                  {{ record.likes || 0 }}
                </span> -->
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <v-pagination
      v-model="pagination.page"
      :length="pagination.total"
      :total-visible="7"
      @input="nextPage()"
      v-if="
        $router['name'] !== 'Dashboard' &&
        recordings.length &&
        pagination.total > 1 &&
        !skeloading
      "
    ></v-pagination>
    <edit-recording-dialog ref="editDialog"></edit-recording-dialog>
    <delete-recording-dialog
      ref="deleteRecordingDialog"
    ></delete-recording-dialog>
    <share-recording-dialog ref="shareRecordingRef"></share-recording-dialog>
    <move-recording-to-folder-dialog
      ref="moveRecordingToFolderRef"
      @load-recordings="getDashboardReceivedRecordings"
    ></move-recording-to-folder-dialog>
    <archive-recording-dialog
      ref="archiveRecordingDialog"
      @load-recordings="getDashboardReceivedRecordings"
    ></archive-recording-dialog>
    <download-recording-dialog
      ref="openDownloadingRef"
    ></download-recording-dialog>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapGetters } from "vuex";
import ResponseHelper from "../utils/ResponseHelper";
import DeleteSelected from "./RecordDetails/DeleteSelected.vue";
import EditRecordingDialog from "./RecordDetails/EditRecordingDialog.vue";
import DeleteRecordingDialog from "./RecordDetails/DeleteRecordingDialog.vue";
import ShareRecordingDialog from "./RecordDetails/ShareRecordingDialog.vue";
import MoveRecordingToFolderDialog from "./RecordDetails/MoveRecordingToFolderDialog.vue";
import ArchiveRecordingDialog from "./RecordDetails/ArchiveRecordingDialog.vue";
import DownloadRecordingDialog from "./RecordDetails/DownloadRecordingDialog.vue";

export default {
  name: "DashboardReceivedRecordings",
  components: {
    EditRecordingDialog,
    DeleteSelected,
    DeleteRecordingDialog,
    ShareRecordingDialog,
    MoveRecordingToFolderDialog,
    ArchiveRecordingDialog,
    DownloadRecordingDialog,
  },
  data() {
    return {
      recordings: [],
      toast: new ResponseHelper(),
      pagination: {
        page: 1,
        total: 1,
      },
      loading: false,
      skeloading: true,
      search: "",
      timerId: null,
      timer: 0,
      select_all: false,
      selected_ids: [],
      hoveredCard: null,
    };
  },
  created() {
    this.getDashboardReceivedRecordings();
    this.$root.$on("search", (data) => {
      this.timer = 1;
      this.timerId = setTimeout(() => {
        if (this.timer > 0) {
          this.search = data;
          this.pagination.page = 1;
          this.getDashboardReceivedRecordings();
        }
        this.timer = 0;
      }, 2000);
    });

    // Ensure the skeleton loader is shown for at least 2 seconds
    setTimeout(() => {
      if (this.recordings.length === 0) {
        this.skeloading = true; // Keep loading if folders are not yet fetched
      }
    }, 2000);
  },
  // updated() {
  //   this.recordings = this.dashboardReceivedMyRecordingDetails;
  // },
  watch: {
    dashboardReceivedMyRecordingDetails: {
      handler(newRecord) {
        this.recordings = newRecord;
      },
    },
    // Watch for changes in recordings
    recordings: {
      handler(newVal) {
        if (newVal.length > 0) {
          setTimeout(() => {
            this.skeloading = false;
          }, 2000);
        } else {
          // If no recordings are found, hide the skeleton loader after 2 seconds
          setTimeout(() => {
            this.skeloading = false;
          }, 2000);
        }
      },
      immediate: true,
    },
  },
  computed: {
    ...mapGetters("recordings", ["dashboardReceivedMyRecordingDetails"]),
  },
  methods: {
    ...mapMutations("recordings", ["SHOW_DETAILS"]),
    ...mapActions("recordings", [
      "dashboard_received_recordings",
      "duplicate_recording",
    ]),

    async getDashboardReceivedRecordings(page) {
      if (!page) {
        page = "?page=" + this.pagination.page + "&search=" + this.search;
      }
      this.loading = true;
      try {
        const response = await this.dashboard_received_recordings(page);
        this.recordings = response.data.data;
        this.pagination.page = response.data["current_page"];
        this.pagination.total = response.data["last_page"];
        this.selected_ids = [];
        this.loading = false;
      } catch (e) {
        this.loading = false;
        this.toast.sendError(e);
        console.log("Error:", e);
      }
    },
    showDetail(item) {
      this.SHOW_DETAILS(item);
      this.$router.push({
        name: "MyRecordingDetails",
        params: { id: item.id },
      });
    },
    nextPage() {
      this.recordings = [];
      this.getDashboardReceivedRecordings();
    },
    async handleDuplicateRecording(record) {
      this.loading = true;
      try {
        const response = await this.duplicate_recording(record);
        this.loading = false;
        this.toast.sendSuccess(response);
        // Hot load datatable
        // this.$store.dispatch("folders/my_folders");
        window.location.reload();
      } catch (e) {
        this.loading = false;
        this.toast.sendError(e);
      }
    },
    deleteRecordingDialog(record) {
      this.$refs.deleteRecordingDialog.openDialog(record);
    },
    editDialog(record) {
      this.$refs.editDialog.openDialog(record);
    },
    callShareRecording(record) {
      this.$refs.shareRecordingRef.openDialog(record);
    },
    callMoveRecordingToFolder(record) {
      this.$refs.moveRecordingToFolderRef.openDialog(record);
    },
    callArchiveRecording(record) {
      this.$refs.archiveRecordingDialog.openDialog(record);
    },
    openDownloadingDialog(record) {
      this.$refs.openDownloadingRef.openDialog(record);
    },
    selectAll() {
      this.selected_ids = [];
      if (this.select_all) {
        this.recordings.forEach((row) => {
          this.selected_ids.push(row.id);
        });
      } else {
        this.selected_ids = [];
      }
    },
    onVideoLoaded(event) {
      // Set video to show first frame (around 2-3 seconds)
      event.target.currentTime = 60;
    },
    onVideoError(event) {
      console.log("Video failed to load:", event);
      // Handle video load error if needed
    },
    truncateTitle(title) {
      return title.length > 25 ? title.substring(0, 25) + "..." : title;
    },
    formatTime(seconds) {
      if (seconds < 0 || isNaN(seconds)) return "00:00";

      const hrs = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      const secs = Math.floor(seconds % 60);

      if (hrs > 0) {
        return `${hrs}:${mins.toString().padStart(2, "0")}:${secs
          .toString()
          .padStart(2, "0")}`;
      }
      return `${mins}:${secs.toString().padStart(2, "0")}`;
    },
  },
  beforeDestroy() {
    clearTimeout(this.timerId);
  },
};
</script>

<style scoped>
.k-folder-dropdown {
  min-width: 160px !important;
  text-align: left !important;
  border-radius: 8px;
  padding: 4px 4px !important;
}

.k-folder-dropdown-item {
  border-radius: 8px;
  padding: 4px 10px;
  min-height: 32px !important;
  transition: background-color 0.2s ease;
}

.k-folder-dropdown-item .v-list-item-title {
  font-size: 11px !important;
  display: flex;
  align-items: center;
  gap: 8px;
  line-height: 1 !important;
  transform: scale(0.7);
  transform-origin: left center;
}

.k-folder-dropdown-item .v-icon,
.k-folder-dropdown-item .svg-inline--fa {
  font-size: 17px !important;
  width: 15px !important;
  height: 15px !important;
}

.k-folder-dropdown-item:hover {
  background-color: #f5f5f5;
}

.no-recording-message {
  font-size: 16px;
  color: #666;
  margin-top: 10px;
}

/* Cards */
.recording-card {
  border-radius: 12px;
  overflow: hidden;
  transition: border-color 0.3s ease;
  cursor: pointer;
  position: relative;
  border: 2px solid transparent;
}

.recording-card:hover {
  border-color: blue;
}

.card-header {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 2;
}

.card-checkbox {
  background: rgba(255, 255, 255, 0.9);
  border-radius: 4px;
  padding: 2px;
}

.video-thumbnail {
  position: relative;
  height: 180px;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  overflow: hidden;
}

.video-preview {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.preview-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-placeholder-fallback {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.play-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  opacity: 0.8;
  transition: opacity 0.3s ease;
}

.recording-card:hover .play-overlay {
  opacity: 1;
}

.video-label {
  position: absolute;
  top: 20px;
  left: 12px;
  background-color: green;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
}

.video-duration {
  position: absolute;
  bottom: 12px;
  right: 12px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.hover-menu {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 3;
}

.menu-trigger-btn {
  background: rgba(0, 0, 0, 0.6) !important;
  border-radius: 50%;
}

.menu-trigger-btn:hover {
  background: rgba(0, 0, 0, 0.8) !important;
}

.card-content {
  padding: 12px !important;
}

.user-time {
  font-size: 12px;
  color: #666;
  line-height: 1.2;
}

.recording-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
  line-height: 1.3;
  cursor: pointer;
  transition: color 0.2s ease;
  white-space: nowrap;
  overflow: hidden;
}

.title-text {
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 13px !important;
}

.separator {
  color: #666;
  font-weight: normal;
}

.time-ago {
  color: #666;
  font-size: 12px;
  font-weight: normal;
}

.recording-title:hover {
  color: #1976d2;
}

.recording-stats {
  display: flex;
  gap: 16px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #666;
}

.recording-card-skeleton {
  border-radius: 12px;
  overflow: hidden;
}

.k-heading {
  height: 50px;
  align-items: center;
}
.k-heading-title {
  font-size: 17px !important;
}

@media (max-width: 960px) {
  .video-thumbnail {
    height: 160px;
  }
}

@media (max-width: 600px) {
  .video-thumbnail {
    height: 140px;
  }

  .card-content {
    padding: 10px !important;
  }

  .recording-title {
    font-size: 14px;
  }
}

/* Card grid system */
.five-cards-row {
  display: flex;
  flex-wrap: wrap;
}

.five-card-col {
  flex: 0 0 20%; /* 100% ÷ 5 = 20% */
  max-width: 20%;
}

@media (max-width: 960px) {
  .five-card-col {
    flex: 0 0 33.333%;
    max-width: 33.333%;
  }
}

@media (max-width: 600px) {
  .five-card-col {
    flex: 0 0 50%;
    max-width: 50%;
  }
}
</style>
